<script>
import cloneDeep from 'lodash.clonedeep';
import Layout from '@layouts/main';
import ReportForm from '@views/reports/form';
import CarUses from '@views/reports/car-uses';
import ServiceTimes from '@views/reports/service-times';
import Expenses from '@views/reports/expenses';
import beforeLeaveAfterEdit from '@mixins/beforeLeaveAfterEdit';
import DailyAllowances from '@views/reports/dailyAllowances/daily-allowances';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ReportModel from '@models/Report';
import _map from 'lodash/map';
import { mapState } from 'vuex';

export default {
  name: 'ReportEdit',
  components: {
    Layout,
    ReportForm,
    CarUses,
    ServiceTimes,
    Expenses,
    FontAwesomeIcon,
    DailyAllowances,
  },
  mixins: [beforeLeaveAfterEdit],
  page() {
    return {
      title: `${this.$t('titles.edit_report')} #${this.$route.params.id}`,
    };
  },
  props: {
    report: {
      type: Object,
      required: true,
      default: null,
    },
    carUses: {
      type: Array,
      required: true,
      default: () => [],
    },
    serviceTimes: {
      type: Array,
      required: true,
      default: () => [],
    },
    expenses: {
      type: Array,
      required: true,
      default: () => [],
    },
    reportsCount: {
      type: Number,
      required: true,
      default: 0,
    },
    allExpensesHaveScan: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    dailyAllowances: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      notCollapsed: {
        car_uses: false,
        expenses: false,
        service_times: false,
        daily_allowances: false,
      },
      breadcrumbItems: [
        {
          text: this.$t('titles.home'),
          to: { name: 'home.index' },
        },
        {
          text: this.$t('titles.reports'),
          to: { name: 'reports.index' },
        },
        {
          text: `${this.$t('titles.edit_report')} #${this.$route.params.id}`,
          active: true,
        },
      ],
      carUsesLocal: [],
      serviceTimesLocal: [],
      expensesLocal: [],
      reportLocal: null,
      dailyAllowancesLocal: [],
      allExpensesHaveScanLocal: this.allExpensesHaveScan,
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),
  },
  mounted() {
    this.carUsesLocal = cloneDeep(this.carUses);
    // TODO this is kinda dirty fix to automatically show row details in service times list
    // check later when there's time is there some better solution
    this.serviceTimesLocal = _map(this.serviceTimes, (serviceTime) => {
      serviceTime._showDetails = true;
      return serviceTime;
    });
    this.expensesLocal = cloneDeep(this.expenses);
    this.reportLocal = cloneDeep(this.report);
    this.dailyAllowancesLocal = cloneDeep(this.dailyAllowances);
  },
  methods: {
    refreshReport() {
      // give it some time to update db
      this.$nextTick(() => {
        setTimeout(() => {
          ReportModel.find(this.report.id).then((response) => {
            this.reportLocal.total_service_time = response.report.total_service_time;
            this.allExpensesHaveScanLocal = response.all_expenses_have_scan;
          });
        }, 500);
      });
    },
    update() {
      this.setUnchanged();
    },
    addItemToList(name, item) {
      this[name].push(item);
    },
    updateItemInList(name, item, key) {
      this.$set(this[name], key, item);
    },
    deleteItemFromList(name, id, key) {
      this[name].splice(key, 1);
    },
  },
};
</script>

<template>
  <Layout>
    <b-breadcrumb :items="breadcrumbItems" />
    <div v-if="reportsCount <= 5" class="text-center text-success lead mb-4">{{ $t('messages.complete_warning') }}</div>
    <b-card
      border-variant="dark"
      header-tag="header"
      header-bg-variant="dark"
      header-class="d-flex flex justify-content-between"
      header-text-variant="white"
      class="mb-3"
    >
      <template v-slot:header>
        <div>{{ $t('titles.report_details') }}</div>
        <b-form-checkbox v-if="reportLocal" v-model="reportLocal.only_costs" :disabled="reportLocal.completed == 1" name="check-button" switch :value="1" :unchecked-value="0">
          <span class="text-light">{{ $t('fields.only_costs') }}</span>
        </b-form-checkbox>
      </template>
      <ReportForm
        v-if="reportLocal"
        v-model="reportLocal"
        :loaded-id="$route.params.id"
        :all-expenses-have-scan="allExpensesHaveScanLocal"
        @submitted="update"
        @entity-changed="initBeforeLeave"
      />
    </b-card>

    <b-card
      v-if="reportLocal && !reportLocal.only_costs"
      :no-body="carUsesLocal.length === 0 && !notCollapsed.car_uses"
      border-variant="dark"
      header-tag="header"
      header-bg-variant="dark"
      header-text-variant="white"
      class="mb-3"
    >
      <template v-slot:header>
        <div>
          {{ $t('titles.car_uses') }}
          <span v-if="!report.locked && !report.completed" class="fa-pull-right cursor-pointer" @click.stop="notCollapsed.car_uses = !notCollapsed.car_uses">
            <span>{{ $t('titles.new_car') }}</span
            >&nbsp;
            <FontAwesomeIcon :icon="['fas', notCollapsed.car_uses ? 'chevron-down' : 'chevron-right']" />
          </span>
        </div>
      </template>
      <CarUses
        ref="carUses"
        :report-id="$route.params.id"
        :car-uses="carUsesLocal"
        :locked="report.locked"
        :completed="report.completed"
        :collapsed.sync="notCollapsed.car_uses"
        @submitted="addItemToList('carUsesLocal', ...arguments)"
        @updated="updateItemInList('carUsesLocal', ...arguments)"
        @deleted="deleteItemFromList('carUsesLocal', ...arguments)"
      />
    </b-card>

    <b-card
      v-if="reportLocal && !reportLocal.only_costs"
      border-variant="dark"
      header-tag="header"
      header-bg-variant="dark"
      header-text-variant="white"
      footer-bg-variant="dark"
      footer-text-variant="white"
      class="mb-3"
    >
      <template v-slot:header>
        <div>
          {{ $t('titles.service_times') }}
          <span
            v-if="!report.locked && !report.completed"
            class="fa-pull-right cursor-pointer"
            @click.stop="notCollapsed.service_times = !notCollapsed.service_times"
          >
            <span>{{ $t('titles.new_service_time') }}</span
            >&nbsp;
            <FontAwesomeIcon :icon="['fas', notCollapsed.service_times ? 'chevron-down' : 'chevron-right']" />
          </span>
        </div>
      </template>
      <template v-slot:footer>
        <div class="text-right"> {{ $t('titles.total_service_time') }} {{ reportLocal.total_service_time }} </div>
      </template>
      <ServiceTimes
        ref="serviceTimes"
        :report-id="$route.params.id"
        :car-uses="carUsesLocal"
        :service-times="serviceTimesLocal"
        :locked="report.locked"
        :completed="report.completed"
        :collapsed.sync="notCollapsed.service_times"
        @submitted="
          addItemToList('serviceTimesLocal', ...arguments);
          refreshReport();
        "
        @updated="
          updateItemInList('serviceTimesLocal', ...arguments);
          refreshReport();
        "
        @deleted="
          deleteItemFromList('serviceTimesLocal', ...arguments);
          refreshReport();
        "
      />
    </b-card>

    <b-card
      v-if="reportLocal"
      :no-body="expensesLocal.length === 0 && !notCollapsed.expenses"
      border-variant="dark"
      header-tag="header"
      header-bg-variant="dark"
      header-text-variant="white"
      class="mb-3">
      <template v-slot:header>
        <div>
          {{ $t('titles.expenses') }}
          <span v-if="!report.locked && !report.completed" class="fa-pull-right cursor-pointer" @click.stop="notCollapsed.expenses = !notCollapsed.expenses">
            <span>{{ $t('titles.new_expense') }}</span
            >&nbsp;
            <FontAwesomeIcon :icon="['fas', notCollapsed.expenses ? 'chevron-down' : 'chevron-right']" />
          </span>
        </div>
      </template>
      <Expenses
        ref="expenses"
        :report="reportLocal"
        :expenses="expensesLocal"
        :collapsed.sync="notCollapsed.expenses"
        @submitted="
          addItemToList('expensesLocal', ...arguments);
          refreshReport();
        "
        @updated="
          updateItemInList('expensesLocal', ...arguments);
          refreshReport();
        "
        @deleted="
          deleteItemFromList('expensesLocal', ...arguments);
          refreshReport();
        "
      />
    </b-card>

    <b-card
      v-if="currentUser.invoice_issuer === 0 && reportLocal && !reportLocal.only_costs"
      border-variant="dark"
      header-tag="header"
      header-bg-variant="dark"
      header-text-variant="white"
      class="mb-3"
      :no-body="dailyAllowancesLocal.length === 0 && !notCollapsed.daily_allowances"
    >
      <template v-slot:header>
        <div>
          {{ $t('titles.allowances') }}
          <span
            v-if="!report.locked && !report.completed"
            class="fa-pull-right cursor-pointer"
            @click.stop="notCollapsed.daily_allowances = !notCollapsed.daily_allowances"
          >
            <span>{{ $t('titles.new_allowance') }}</span
            >&nbsp;
            <FontAwesomeIcon :icon="['fas', notCollapsed.daily_allowances ? 'chevron-down' : 'chevron-right']" />
          </span>
        </div>
      </template>
      <DailyAllowances
        ref="daily_allowances"
        :report-id="$route.params.id"
        :daily-allowances="dailyAllowancesLocal"
        :locked="report.locked"
        :completed="report.completed"
        :collapsed.sync="notCollapsed.daily_allowances"
        @submitted="addItemToList('dailyAllowancesLocal', ...arguments)"
        @updated="updateItemInList('dailyAllowancesLocal', ...arguments)"
        @deleted="deleteItemFromList('dailyAllowancesLocal', ...arguments)"
      />
    </b-card>
  </Layout>
</template>
<style type="text/css" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
